import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BorderBeam } from 'border-beam';
import { useReducedMotion } from 'framer-motion';
import { History, FolderKanban, X, FolderOpen, PanelLeftClose, MessageSquare, FileText, Search, PenLine, HelpCircle, FilePlus } from 'lucide-react';
import { Button } from '../ui/button';
import { HomeComposer, HomeComposerHandle } from './HomeComposer';
import { type SlashCommand } from './slash-commands';
import { Progress } from '../ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Label } from '../ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../ui/resizable';
import { useBoundStore } from '../../store';
import {
  getProjectFiles,
  getProjectPlaybooks,
  uploadProjectFile,
  linkPlaybook,
  linkAllPlaybooks,
  unlinkPlaybook,
  deleteProjectFile,
  ProjectFile,
  ProjectPlaybook,
} from '../../services/Project';
import { ChatHistoryDrawer } from './ChatHistoryDrawer';
import { ContextPanel } from './ContextPanel';
import { FileDropZone } from './FileDropZone';
import { FileViewerSheet } from './FileViewerSheet';
import { GenerateContractDialog, GenerateContractValues } from './GenerateContractDialog';
import { toast } from 'sonner';
import { useI18n } from '../../i18n';
import { useWorkspace } from '../../workspace/WorkspaceProvider';
import { Hint } from '../../onboarding/Hint';

export const QAgentHome: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const [isGenerateDialogOpen, setIsGenerateDialogOpen] = useState(false);
  const [isStartingGenerate, setIsStartingGenerate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const prefersReducedMotion = useReducedMotion();
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [isContextPanelOpen, setIsContextPanelOpen] = useState(false);
  const homeComposerRef = useRef<HomeComposerHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Project context state
  const [projectFiles, setProjectFiles] = useState<ProjectFile[]>([]);
  const [projectPlaybooks, setProjectPlaybooks] = useState<ProjectPlaybook[]>([]);
  const [activeFileIds, setActiveFileIds] = useState<string[]>([]);
  const [activePlaybookId, setActivePlaybookId] = useState<string | null>(null);
  const [loadingProjectData, setLoadingProjectData] = useState(false);

  // Context panel section expansion state
  const [filesExpanded, setFilesExpanded] = useState(true);
  const [playbooksExpanded, setPlaybooksExpanded] = useState(true);

  // File upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Link playbook dialog state
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [selectedPlaybookToLink, setSelectedPlaybookToLink] = useState('');
  const [isLinking, setIsLinking] = useState(false);

  // Delete file dialog state
  const [fileToDelete, setFileToDelete] = useState<ProjectFile | null>(null);
  const [isDeletingFile, setIsDeletingFile] = useState(false);

  // Unlink playbook dialog state
  const [playbookToUnlink, setPlaybookToUnlink] = useState<ProjectPlaybook | null>(null);
  const [isUnlinkingPlaybook, setIsUnlinkingPlaybook] = useState(false);

  // File viewer state
  const [fileToView, setFileToView] = useState<ProjectFile | null>(null);
  const [isUploadingDrop, setIsUploadingDrop] = useState(false);

  const user = useBoundStore((state) => state.user);
  const projects = useBoundStore((state) => state.projects);
  const fetchProjects = useBoundStore((state) => state.fetchProjects);
  const createProject = useBoundStore((state) => state.createProject);
  const fetchQAgentChats = useBoundStore((state) => state.fetchQAgentChats);
  const createQAgentChat = useBoundStore((state) => state.createQAgentChat);
  const qagentChats = useBoundStore((state) => state.qagentChats);
  const playbooks = useBoundStore((state) => state.playbooks);
  const fetchPlaybooks = useBoundStore((state) => state.fetchPlaybooks);

  // Fetch data on mount (always fetch fresh projects for accurate file counts)
  useEffect(() => {
    fetchQAgentChats();
    fetchProjects();
  }, [fetchQAgentChats, fetchProjects]);

  // Load playbooks on mount so they're available to @-mention in the composer
  // (and for the link dialog).
  useEffect(() => {
    if (playbooks.length === 0) {
      fetchPlaybooks();
    }
    // Only on mount; fetchPlaybooks is stable from the store.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load project files and playbooks when selectedProjectId changes
  useEffect(() => {
    if (!selectedProjectId) {
      setProjectFiles([]);
      setProjectPlaybooks([]);
      setActiveFileIds([]);
      setActivePlaybookId(null);
      setIsContextPanelOpen(false);
      return;
    }

    const loadProjectData = async () => {
      setLoadingProjectData(true);
      try {
        const [filesResponse, playbooksResponse] = await Promise.all([
          getProjectFiles(selectedProjectId),
          getProjectPlaybooks(selectedProjectId),
        ]);

        if (filesResponse.success && filesResponse.data) {
          setProjectFiles(filesResponse.data);
          setActiveFileIds(
            filesResponse.data
              .filter(f => f.status === 'ready')
              .map(f => f.id)
          );
        }

        if (playbooksResponse.success && playbooksResponse.data) {
          setProjectPlaybooks(playbooksResponse.data);
        }
      } catch (error) {
        console.error('[QAgentHome] Failed to load project data:', error);
      } finally {
        setLoadingProjectData(false);
      }
    };

    loadProjectData();
  }, [selectedProjectId]);

  const selectedProject = projects.find(p => p.id === selectedProjectId);

  // Get available playbooks (not already linked to this project)
  const linkedPlaybookIds = new Set(projectPlaybooks.map((p) => p.playbookId));
  const availablePlaybooks = playbooks.filter((p) => !linkedPlaybookIds.has(p.id));

  // Drag-and-drop a document onto the chat. If a project is already
  // selected, append the file to it. Otherwise create a new project named
  // after the file, attach the file, and route the user there.
  const handleFilesDropped = useCallback(
    async (files: FileList) => {
      const file = files[0];
      if (!file || isUploadingDrop) return;
      setIsUploadingDrop(true);
      try {
        let targetProjectId = selectedProjectId;
        if (!targetProjectId) {
          // Strip extension for the project name; fall back to filename.
          const baseName = file.name.replace(/\.[^.]+$/, '') || file.name;
          const project = await createProject({ name: baseName });
          if (!project) {
            // createProject sets state.projectsError (e.g. trial cap hit);
            // ProjectsSlice already wrote the message to error - surface it.
            const err = useBoundStore.getState().projectsError;
            toast.error(err || t.qagentHome.toasts.createProjectFailed);
            return;
          }
          targetProjectId = project.id;
        }
        const upload = await uploadProjectFile(targetProjectId, file);
        if (!upload.success) {
          const err =
            upload.errors?.[0]?.detail ||
            upload.errors?.[0]?.title ||
            t.qagentHome.toasts.uploadFileFailed;
          toast.error(err);
          return;
        }
        toast.success(t.qagentHome.toasts.fileAttached(file.name));
        // Reference every available playbook so the user can apply one with a
        // single click in the chat. Best-effort: a failure here must not block
        // the user from landing in their new project.
        await linkAllPlaybooks(targetProjectId).catch((err) =>
          console.error('[QAgentHome] Failed to auto-link playbooks:', err),
        );
        // Route to the project chat so the user lands where the file lives.
        navigate(`/qagent/project/${targetProjectId}`);
      } finally {
        setIsUploadingDrop(false);
      }
    },
    [selectedProjectId, createProject, navigate, isUploadingDrop, t],
  );

  // Store playbooks in the shape the @-mention menu expects.
  const mentionablePlaybooks: ProjectPlaybook[] = useMemo(
    () =>
      playbooks.map((p) => ({
        id: p.id,
        playbookId: p.id,
        playbookName: p.name,
        playbookAgreementType: p.agreementType,
        createdAt: p.createdAt,
      })),
    [playbooks],
  );

  const handleSelectPlaybook = (playbookId: string | null) => {
    setActivePlaybookId(playbookId);
  };

  // File upload handlers
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedProjectId) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const result = await uploadProjectFile(selectedProjectId, file, (progress) => {
        setUploadProgress(progress);
      });

      if (result.success && result.data) {
        toast.success(t.qagentHome.toasts.fileUploadedSuccess(file.name));
        const filesResponse = await getProjectFiles(selectedProjectId);
        if (filesResponse.success && filesResponse.data) {
          setProjectFiles(filesResponse.data);
          setActiveFileIds(prev => [...prev, result.data!.id]);
        }
        // Refresh projects to update file count
        fetchProjects();
      } else {
        toast.error(t.qagentHome.toasts.uploadFileFailed);
      }
    } catch (error) {
      toast.error(t.qagentHome.toasts.uploadError);
      console.error('Upload error:', error);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Playbook linking handler
  const handleLinkPlaybook = async () => {
    if (!selectedPlaybookToLink || !selectedProjectId) return;

    setIsLinking(true);
    try {
      const response = await linkPlaybook(selectedProjectId, { playbookId: selectedPlaybookToLink });
      if (response.success) {
        toast.success(t.qagentHome.toasts.playbookLinkedSuccess);
        setIsLinkDialogOpen(false);
        setSelectedPlaybookToLink('');
        const playbooksResponse = await getProjectPlaybooks(selectedProjectId);
        if (playbooksResponse.success && playbooksResponse.data) {
          setProjectPlaybooks(playbooksResponse.data);
        }
      } else {
        const errorMsg = response.errors?.[0]?.title || t.qagentHome.toasts.linkPlaybookFailed;
        toast.error(errorMsg);
      }
    } catch (error) {
      toast.error(t.qagentHome.toasts.linkError);
      console.error('Link error:', error);
    } finally {
      setIsLinking(false);
    }
  };

  // Delete file handler
  const handleDeleteFile = async () => {
    if (!fileToDelete || !selectedProjectId) return;

    setIsDeletingFile(true);
    try {
      const response = await deleteProjectFile(selectedProjectId, fileToDelete.id);
      if (response.success) {
        toast.success(t.qagentHome.toasts.fileDeleted(fileToDelete.name));
        setProjectFiles(prev => prev.filter(f => f.id !== fileToDelete.id));
        setActiveFileIds(prev => prev.filter(id => id !== fileToDelete.id));
        // Refresh projects to update file count
        fetchProjects();
      } else {
        toast.error(t.qagentHome.toasts.deleteFileFailed);
      }
    } catch (error) {
      toast.error(t.qagentHome.toasts.deleteError);
      console.error('Delete error:', error);
    } finally {
      setIsDeletingFile(false);
      setFileToDelete(null);
    }
  };

  // Unlink playbook handler
  const handleUnlinkPlaybook = async () => {
    if (!playbookToUnlink || !selectedProjectId) return;

    setIsUnlinkingPlaybook(true);
    try {
      const response = await unlinkPlaybook(selectedProjectId, playbookToUnlink.playbookId);
      if (response.success) {
        toast.success(t.qagentHome.toasts.playbookUnlinked(playbookToUnlink.playbookName));
        setProjectPlaybooks(prev => prev.filter(p => p.id !== playbookToUnlink.id));
        if (activePlaybookId === playbookToUnlink.playbookId) {
          setActivePlaybookId(null);
        }
      } else {
        toast.error(t.qagentHome.toasts.unlinkPlaybookFailed);
      }
    } catch (error) {
      toast.error(t.qagentHome.toasts.unlinkError);
      console.error('Unlink error:', error);
    } finally {
      setIsUnlinkingPlaybook(false);
      setPlaybookToUnlink(null);
    }
  };

  // Create a new chat (optionally within the selected project) and navigate to
  // it, handing off the given state (an initial message and/or an initial
  // skill from a slash command).
  const startChat = async (state: Record<string, unknown>) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const chat = await createQAgentChat({
        projectId: selectedProjectId || undefined,
      });
      if (!chat?.id) {
        throw new Error('Chat created but no ID returned');
      }
      navigate(`/qagent/${chat.id}`, { state });
    } catch (error) {
      console.error('[QAgentHome] Error creating chat:', error);
      setIsLoading(false);
    }
  };

  const handleHomeSubmit = (text: string) => {
    if (!text.trim()) return;
    startChat({ initialMessage: text.trim() });
  };

  const openGenerateDialog = useCallback(() => {
    setIsGenerateDialogOpen(true);
  }, []);

  const handleSubmitGenerate = async (values: GenerateContractValues) => {
    if (isStartingGenerate) return;
    setIsStartingGenerate(true);
    try {
      const chat = await createQAgentChat({
        projectId: selectedProjectId || undefined,
      });
      if (!chat?.id) throw new Error('Failed to create chat');
      setIsGenerateDialogOpen(false);
      navigate(`/qagent/${chat.id}`, {
        state: {
          openGenerate: true,
          generateDocType: values.docType,
          generateDescription: values.description,
          generatePlaybookId: values.playbookId,
        },
      });
    } catch (error) {
      console.error('[QAgentHome] Error starting first draft:', error);
      toast.error(t.qagentHome.toasts.startContractFailed);
    } finally {
      setIsStartingGenerate(false);
    }
  };

  // Open the picker dialog when arriving via the sidebar's "New Contract" entry.
  useEffect(() => {
    const state = location.state as { openGenerateDialog?: boolean } | null;
    if (state?.openGenerateDialog) {
      openGenerateDialog();
      window.history.replaceState({}, '', location.pathname);
    }
  }, [location.state, location.pathname, openGenerateDialog]);

  // A slash command chosen from the home composer. /generate opens the draft
  // picker; a skill (e.g. /review) launches a new chat already in that skill,
  // where the user then attaches a document.
  const handleHomeSkill = (command: SlashCommand) => {
    if (command.id === 'generate') {
      openGenerateDialog();
      return;
    }
    if (command.type === 'skill' && command.skillId) {
      startChat({ initialSkillId: command.skillId });
    }
  };

  const { greetingTemplate } = useWorkspace();
  const defaultGreeting = user?.firstName
    ? t.qagentHome.greeting.welcome(user.firstName)
    : t.qagentHome.greeting.fallback;
  // A workspace greeting (e.g. "Welcome back, {firstName} — ready to make par?")
  // takes precedence when configured; {firstName} is interpolated client-side.
  const greeting = greetingTemplate
    ? greetingTemplate.replace(/\{firstName\}/g, user?.firstName ?? "").trim()
    : defaultGreeting;
  // Render the greeting across two lines: the personal "Welcome back, {name}."
  // stays on top and any workspace tagline that follows (e.g. "Golf, your game,
  // our passion. ⛳") drops to a second line. We split on the first sentence
  // break; greetings without one (the default "Welcome, {name}") stay single-line.
  const greetingMatch = greeting.match(/^(.*?[.!?])\s+(.+)$/s);
  const greetingLines = greetingMatch
    ? [greetingMatch[1], greetingMatch[2]]
    : [greeting];

  // Main content area
  const MainContent = (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          {selectedProjectId && (
            <Button
              variant={isContextPanelOpen ? "ghost" : "outline"}
              size={isContextPanelOpen ? "icon" : "sm"}
              onClick={() => setIsContextPanelOpen(!isContextPanelOpen)}
              className={isContextPanelOpen ? "text-muted-foreground" : "gap-2 text-muted-foreground hover:text-foreground"}
            >
              {isContextPanelOpen ? (
                <PanelLeftClose className="h-5 w-5" />
              ) : (
                <>
                  <FolderOpen className="h-4 w-4" />
                  <span>{t.qagentHome.header.resources}</span>
                </>
              )}
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsHistoryOpen(true)}
            title={t.qagentHome.header.recentChats}
            className="text-muted-foreground hover:text-foreground"
          >
            <History className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Centered Welcome & Input */}
      <div className="flex-1 flex flex-col items-center justify-center px-4">
        <h1 className="text-3xl md:text-4xl font-light text-foreground mb-3 text-center text-balance">
          {greetingLines.map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </h1>
        <p className="text-lg md:text-xl text-muted-foreground mb-6">
          {t.qagentHome.greeting.subtitle}
        </p>

        {/* Guided prompts */}
        <div data-tour="home-prompts" className="flex flex-wrap justify-center gap-2 mb-6 max-w-xl">
          <button
            onClick={openGenerateDialog}
            title={t.qagentHome.prompts.firstDraft.tooltip}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-brand/40 bg-brand-light/30 hover:bg-brand-light/60 hover:border-brand transition-all text-[13px] text-brand font-medium"
          >
            <FilePlus className="h-3.5 w-3.5 shrink-0" />
            {t.qagentHome.prompts.firstDraft.label}
          </button>
          {[
            { icon: FileText, label: t.qagentHome.prompts.reviewContract.label, prompt: t.qagentHome.prompts.reviewContract.prompt },
            { icon: Search, label: t.qagentHome.prompts.checkCompliance.label, prompt: t.qagentHome.prompts.checkCompliance.prompt },
            { icon: PenLine, label: t.qagentHome.prompts.suggestImprovements.label, prompt: t.qagentHome.prompts.suggestImprovements.prompt },
            { icon: HelpCircle, label: t.qagentHome.prompts.askQuestion.label, prompt: t.qagentHome.prompts.askQuestion.prompt },
          ].map((item) => (
            <button
              key={item.label}
              onClick={() => homeComposerRef.current?.setText(item.prompt)}
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-border/40 bg-background hover:border-brand/40 hover:bg-brand-light/20 transition-all text-[13px] text-muted-foreground hover:text-foreground"
            >
              <item.icon className="h-3.5 w-3.5 shrink-0" />
              {item.label}
            </button>
          ))}
        </div>

        {/* Input Container with Project Selector */}
        <div className="w-full max-w-2xl space-y-2">
          {/* Project Selector - attached above input */}
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`gap-2 text-xs ${
                    selectedProjectId
                      ? 'text-brand bg-brand-light border border-brand/30'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <FolderKanban className="h-3.5 w-3.5" />
                  {selectedProject ? (
                    <span className="max-w-[150px] truncate">{selectedProject.name}</span>
                  ) : (
                    <span>{t.qagentHome.input.addProjectContext}</span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {projects.length === 0 ? (
                  <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                    {t.qagentHome.input.noProjectsYet}
                  </div>
                ) : (
                  projects.map((project) => {
                    const existingChat = qagentChats.find(c => c.projectId === project.id);
                    return (
                      <DropdownMenuItem
                        key={project.id}
                        onClick={() => {
                          if (existingChat) {
                            navigate(`/qagent/${existingChat.id}`);
                          } else {
                            setSelectedProjectId(project.id);
                          }
                        }}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <FolderKanban className="h-4 w-4 text-muted-foreground" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm truncate">{project.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {t.qagentHome.input.fileCount(project.fileCount)}
                            {existingChat && t.qagentHome.input.hasChat}
                          </div>
                        </div>
                        {existingChat && (
                          <MessageSquare className="h-3.5 w-3.5 text-brand flex-shrink-0" />
                        )}
                      </DropdownMenuItem>
                    );
                  })
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {selectedProjectId && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedProjectId(null)}
                className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>

          {/* Input Box: supports @playbook mentions and /commands. The beam invites the first
              message: it fades out once one is sent (the chat opens on its own page, without it)
              and stays off for reduced motion, which its rotating preset doesn't handle itself.
              The wrapper clips by default, which would hide the @ and / menus opening above. */}
          <BorderBeam
            size="md"
            colorVariant="forest"
            theme="light"
            active={!isLoading && !prefersReducedMotion}
            style={{ overflow: 'visible' }}
          >
            <HomeComposer
              ref={homeComposerRef}
              playbooks={mentionablePlaybooks}
              placeholder={t.qagentHome.input.placeholder}
              disabled={isLoading}
              sendAriaLabel={t.qagentHome.input.placeholder}
              onSubmit={handleHomeSubmit}
              onSlashCommand={handleHomeSkill}
            />
          </BorderBeam>
          <Hint id="tip-drop-document" title={t.onboarding.tips.dropDocument.title}>
            {t.onboarding.tips.dropDocument.body}
          </Hint>
        </div>
      </div>
    </div>
  );

  return (
    <FileDropZone
      onFilesDropped={handleFilesDropped}
      isUploading={isUploadingDrop}
      message={
        selectedProjectId
          ? selectedProject?.name
            ? t.qagentHome.dropZone.attachToProject(selectedProject.name)
            : t.qagentHome.dropZone.attachToProjectFallback
          : t.qagentHome.dropZone.startNewProject
      }
    >
    <div className="h-[calc(100vh-2rem)] bg-background">
      {selectedProjectId && isContextPanelOpen ? (
        <ResizablePanelGroup orientation="horizontal" className="h-full">
          {/* ContextPanel is laid out at min 240px; narrower would clip its row actions */}
          <ResizablePanel defaultSize="25%" minSize="240px" maxSize="100%">
            <div className="h-full animate-in slide-in-from-left duration-200">
              <ContextPanel
                projectId={selectedProjectId}
                projectName={selectedProject?.name || null}
                files={projectFiles}
                playbooks={projectPlaybooks}
                activeFileIds={activeFileIds}
                activePlaybookId={activePlaybookId}
                onSelectPlaybook={handleSelectPlaybook}
                onUploadClick={handleUploadClick}
                onLinkPlaybookClick={() => setIsLinkDialogOpen(true)}
                onCreatePlaybookClick={() => navigate('/playbooks?new=create')}
                onUploadPlaybookClick={() => navigate('/playbooks?new=import')}
                onDeleteFile={setFileToDelete}
                onViewFile={setFileToView}
                onViewPlaybook={() => {}} // Not supported in home view
                onUnlinkPlaybook={setPlaybookToUnlink}
                isLoading={loadingProjectData}
                filesExpanded={filesExpanded}
                playbooksExpanded={playbooksExpanded}
                onFilesExpandedChange={setFilesExpanded}
                onPlaybooksExpandedChange={setPlaybooksExpanded}
              />
            </div>
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize="75%">
            {MainContent}
          </ResizablePanel>
        </ResizablePanelGroup>
      ) : (
        <div className="h-full">
          {MainContent}
        </div>
      )}

      {/* Chat History Drawer */}
      <ChatHistoryDrawer
        open={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
      />

      {/* Hidden file input for uploads */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        accept=".pdf,.doc,.docx,.txt,.md"
      />

      {/* First-draft / generate contract picker */}
      <GenerateContractDialog
        open={isGenerateDialogOpen}
        onOpenChange={setIsGenerateDialogOpen}
        onSubmit={handleSubmitGenerate}
        isSubmitting={isStartingGenerate}
        initialPlaybookId={activePlaybookId}
      />

      {/* Upload progress dialog */}
      <Dialog open={isUploading}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t.qagentHome.uploadDialog.title}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Progress value={uploadProgress} className="w-full" />
            <p className="text-sm text-muted-foreground mt-2 text-center">
              {t.qagentHome.uploadDialog.percentComplete(uploadProgress)}
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Link Playbook Dialog */}
      <Dialog open={isLinkDialogOpen} onOpenChange={setIsLinkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.qagentHome.linkPlaybookDialog.title}</DialogTitle>
            <DialogDescription>
              {t.qagentHome.linkPlaybookDialog.description}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="playbook">{t.qagentHome.linkPlaybookDialog.playbookLabel}</Label>
              <Select value={selectedPlaybookToLink} onValueChange={setSelectedPlaybookToLink}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t.qagentHome.linkPlaybookDialog.selectPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  {availablePlaybooks.length === 0 ? (
                    <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                      {t.qagentHome.linkPlaybookDialog.noAvailablePlaybooks}
                    </div>
                  ) : (
                    availablePlaybooks.map((pb) => (
                      <SelectItem key={pb.id} value={pb.id}>
                        {pb.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsLinkDialogOpen(false);
                setSelectedPlaybookToLink('');
              }}
              disabled={isLinking}
            >
              {t.common.actions.cancel}
            </Button>
            <Button onClick={handleLinkPlaybook} disabled={isLinking || !selectedPlaybookToLink}>
              {isLinking ? t.qagentHome.linkPlaybookDialog.linking : t.qagentHome.linkPlaybookDialog.submit}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete File Confirmation Dialog */}
      <Dialog open={!!fileToDelete} onOpenChange={(open) => !open && setFileToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.qagentHome.deleteFileDialog.title}</DialogTitle>
            <DialogDescription>
              {t.qagentHome.deleteFileDialog.description(fileToDelete?.name ?? '')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setFileToDelete(null)}
              disabled={isDeletingFile}
            >
              {t.common.actions.cancel}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteFile}
              disabled={isDeletingFile}
            >
              {isDeletingFile ? t.qagentHome.deleteFileDialog.deleting : t.common.actions.delete}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unlink Playbook Confirmation Dialog */}
      <Dialog open={!!playbookToUnlink} onOpenChange={(open) => !open && setPlaybookToUnlink(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.qagentHome.unlinkPlaybookDialog.title}</DialogTitle>
            <DialogDescription>
              {t.qagentHome.unlinkPlaybookDialog.description(playbookToUnlink?.playbookName ?? '')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPlaybookToUnlink(null)}
              disabled={isUnlinkingPlaybook}
            >
              {t.common.actions.cancel}
            </Button>
            <Button
              variant="destructive"
              onClick={handleUnlinkPlaybook}
              disabled={isUnlinkingPlaybook}
            >
              {isUnlinkingPlaybook ? t.qagentHome.unlinkPlaybookDialog.unlinking : t.qagentHome.unlinkPlaybookDialog.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* File Viewer Sheet */}
      <FileViewerSheet
        file={fileToView}
        projectId={selectedProjectId || ''}
        isOpen={!!fileToView}
        onClose={() => setFileToView(null)}
      />
    </div>
    </FileDropZone>
  );
};

export default QAgentHome;
